import './Home.css';
import { Box, Button, Container, Divider, Grid, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useEffect, useState } from 'react';
import Carousel from '../../Components/Carousel/Carousel';
import { motion } from "framer-motion";
import Card from '../../Components/Card/Card';
import PricingCard from '../../Components/PricingCard/PricingCard';
import { useNavigate } from 'react-router-dom';
import FAQ from '../../Components/FAQ/FAQ';
import { HOME_CARDS, PLAN_CARDS } from '../../utils/constants';

interface CardLabels {
  title: string;
  description: string;
}

interface PlanLabels {
  title: string;
  subtitle: string;
  features: string[];
  cta: string;
}

function Home() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  // useEffect(() => {
  //   if (window != null) {
  //     window.scrollTo({ top: 0, behavior: 'smooth' });
  //   }
  // }, [])

  // Poppins loads async (Google Fonts <link> in index.html). If the hero
  // animates in before the font swaps from the fallback, the text's box
  // height changes mid/just-after the animation and the whole hero shifts —
  // looks like the animation "started lower" than it should. Hold the intro
  // at its initial (invisible) state until fonts are ready so it animates in
  // with final, stable metrics. Falls back to "ready" immediately if the
  // Font Loading API is unsupported, so the animation never gets stuck.
  const [fontsReady, setFontsReady] = useState(
    () => typeof document === 'undefined' || !document.fonts || document.fonts.status === 'loaded'
  );

  useEffect(() => {
    if (fontsReady || typeof document === 'undefined' || !document.fonts) return;
    document.fonts.ready.then(() => setFontsReady(true));
  }, [fontsReady]);

  const subtitleWords = t("home.subtitle.words", { returnObjects: true }) as string[];
  const featureLabels = t("home.why.features", { returnObjects: true }) as CardLabels[];
  const planLabels = t("home.pricing.cards", { returnObjects: true }) as PlanLabels[];

  return (
    <div className="section-home">
      <Container className='section-hero-container' maxWidth="lg">
        <Grid
          container
          className="home-hero-container"
          direction="column"
          justifyContent="center"
          alignItems="center"
          paddingTop="6rem"
          paddingBottom="4rem"
        >
          <Grid size={{ xs: 12, sm: 10 }} display={'flex'} flexDirection={'column'} alignItems={'center'}>
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              animate={fontsReady ? { opacity: 1, y: 0 } : undefined}
              transition={{ duration: 0.8 }}
            >
              <Typography className='title-home' variant="h3" gutterBottom component="h1" align='center'>
                {t("home.title1")} <span style={{ color: `#e8e7e7` }}>{t("home.title2")}</span>
              </Typography>
            </motion.div>
            <motion.div
              initial={{ opacity: 0 }}
              animate={fontsReady ? { opacity: 1 } : undefined}
              transition={{ delay: 0.5, duration: 0.8 }}
              style={{ display: 'flex', justifyContent: 'center' }}
            >
              <Typography className='text-body pretty-text' variant="body1" gutterBottom component="p" align='center' sx={{ color: 'text.secondary', width: '80%', textAlign: 'center' }}>
                {t("home.subtitle.main")} <span style={{ color: 'white' }}>{subtitleWords[0]}</span>, <span style={{ color: 'white' }}>{subtitleWords[1]}</span>, {t("home.default.and")} <span style={{ color: 'white' }}>{subtitleWords[2]}</span>.
              </Typography>
            </motion.div>
            <motion.div
              initial={{ opacity: 0 }}
              animate={fontsReady ? { opacity: 1 } : undefined}
              transition={{ delay: 1, duration: 0.6 }}
            >
              <Button variant='contained' size="large" onClick={() => navigate("/signup")}>{t("home.commit")}</Button>
            </motion.div>
          </Grid>
        </Grid>
      </Container>
      <Container className='section-carousel-container' maxWidth={false}>
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 1.2, duration: 0.8, ease: 'easeOut' }}
        >
          <Carousel />
        </motion.div>
      </Container>
      <Container className='section-us-container' maxWidth="lg">
        <Grid
          container
          className="section-us-grid"
          direction="column"
          justifyContent="center"
          alignItems="center"
          paddingTop="4rem"
          paddingBottom="6.5rem"
        >
          <Grid size={{ xs: 12, sm: 10 }}>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <Typography className='subtitle-body' variant="h4" gutterBottom component="h2" align='center' sx={{ color: 'text.primary' }}>
                {t("home.why.title")}
              </Typography>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3, duration: 0.8 }}
              style={{ display: 'flex', justifyContent: 'center' }}
            >
              <Typography className='text-body why-text-body pretty-text' variant="body1" gutterBottom component="p" align='center' sx={{ color: 'text.secondary' }}>
                {t("home.why.description")}
              </Typography>
            </motion.div>
            <Box
              sx={{
                width: '100%',
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(min(300px, 100%), 1fr))',
                gap: 2,
                alignItems: 'stretch'
              }}
            >
              {HOME_CARDS.map((card, index) => (
                <motion.div
                  key={`feature-${card.id}-${index}`}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: card.delay, duration: 0.8 }}
                >
                  <Card index={index} labels={featureLabels[index]} {...card} />
                </motion.div>
              ))}
            </Box>
          </Grid>
        </Grid>
      </Container>
      <Divider aria-hidden="true" variant='fullWidth' sx={{ borderColor: 'rgba(51, 59, 77, 0.6)', width: '100%' }} />
      <Container className='section-pricing-container' maxWidth="lg">
        <Grid
          container
          className="home-pricing-container"
          direction="column"
          justifyContent="center"
          alignItems="center"
          paddingTop="6.5rem"
          paddingBottom="6.5rem"
        >
          <Grid size={10}>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              style={{ display: 'flex', justifyContent: 'center' }}
            >
              <Typography
                variant="subtitle1"
                align='center'
                sx={{ color: "text.secondary" }}
              >
                {t("home.pricing.title")}
              </Typography>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.3 }}
              >
              <Typography
                className='subtitle-body'
                variant="h4"
                gutterBottom
                component="h2"
                align='center'
                sx={{ color: 'text.primary' }}
              >
                {t("home.pricing.subtitle")}
              </Typography>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, delay: 0.4 }}
              viewport={{ once: true }}
              style={{ display: 'flex', justifyContent: 'center' }}
            >
              <Typography
                className='text-body pricing-text-body pretty-text'
                variant="body1"
                gutterBottom
                component="p"
                align='center'
                sx={{ color: 'text.secondary' }}
              >
                {t("home.pricing.description")}
              </Typography>
            </motion.div>
            <Box
              sx={{
                width: '100%',
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(auto, 400px))',
                gap: 2,
                alignItems: 'center',
                placeContent: 'center'
              }}
            >
              {PLAN_CARDS.map((plan, index) => (
                <motion.div
                  key={`plan-${plan.id}-${index}`}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: plan.delay, duration: 0.8 }}
                  style={{ height: "100%" }}
                >
                  <PricingCard index={index} labels={planLabels[index]} {...plan}  />
                </motion.div>
              ))}
            </Box>
          </Grid>
        </ Grid>
      </Container>
      <Divider aria-hidden="true" variant='fullWidth' sx={{ borderColor: 'rgba(51, 59, 77, 0.6)', width: '100%' }} />
      <Container className='section-cta-container' maxWidth="lg">
        <Grid
          container
          className="home-cta-container"
          direction="column"
          justifyContent="center"
          alignItems="center"
          paddingTop="4.5rem"
          paddingBottom="4.5rem"
        >
          <Grid size={{ xs: 12, sm: 10 }} display={'flex'} flexDirection={'column'} alignItems={'center'}>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <Typography className='subtitle-body pretty-text' variant="h4" gutterBottom component="h2" align='center' sx={{ color: 'text.primary' }}>
                {t("home.cta.title")}
              </Typography>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.5, duration: 0.8 }}
            >
              <Typography className='text-body pretty-text' variant="body1" gutterBottom component="p" align='center' sx={{ color: 'text.secondary' }}>
                {t("home.cta.description")}
              </Typography>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 1, duration: 0.6 }}
            >
              <Button variant='outlined' size="large" onClick={() => navigate("/signup")}>{t("home.cta.commit")}</Button>
            </motion.div>
          </Grid>
        </Grid>
      </Container>
      <Divider aria-hidden="true" variant='fullWidth' sx={{ borderColor: 'rgba(51, 59, 77, 0.6)', width: '100%' }} />
      <Container className='section-faq-container' maxWidth="lg">
        <Grid
          container
          className="home-faq-container"
          direction="column"
          justifyContent="center"
          alignItems="center"
          paddingTop="6.5rem"
          paddingBottom="6.5rem"
        >
          <Grid size={{ xs: 12, sm: 10 }} display={'flex'} flexDirection={'column'} alignItems={'center'}>
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8 }}
              >
                <Typography className='subtitle-body' variant="h4" gutterBottom component="h2" align='center' sx={{ color: 'text.primary' }}>
                  {t('home.faq.title')}
                </Typography>
              </motion.div>
              <FAQ />
          </Grid>
        </Grid>
      </Container>
    </div>
  );
}

export default Home;
